import { useEffect, useState } from 'react';

import { getPublicWaveformData } from 'api/controllers/ecg-capture-controller';
import { getPublicAccountData } from 'api/controllers/account-controller';
import { ECGWaveFormResponseDto, AccountPublicResponseDto } from 'types/types';
import { getFilteredECGSamples } from 'utils';
import { CancelablePromise } from 'api/request/cancelable-promise';

const useSharedWaveform = (waveformData: string) => {
  const [ecgWaveForm, setEcgWaveForm] = useState<null | ECGWaveFormResponseDto>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountData, setAccountData] = useState<null | AccountPublicResponseDto>(null);

  useEffect(() => {
    setIsLoading(true);
    const [waveformId, expiredTimestamp, patientId] = waveformData.split('/');

    if (+expiredTimestamp <= Date.now()) {
      setError(new Error('Expired waveform'));
      setIsLoading(false);

      return () => null;
    }

    const getEcgWaveForm = getPublicWaveformData(waveformId);
    const getAccountData = getPublicAccountData(patientId);
    const getSharedEcgData = async () => {
      try {
        const [ecgData, patientData] = await Promise.all([getEcgWaveForm, getAccountData]);
        const filteredEcg = { ...ecgData, samples: getFilteredECGSamples(ecgData.samples, ecgData.sampleRate) };

        setEcgWaveForm(filteredEcg);
        setAccountData(patientData);
      } catch (error) {
        setError(error);
        setEcgWaveForm(null);
        setAccountData(null);
      } finally {
        setIsLoading(false);
      }
    };

    getSharedEcgData();

    return () => {
      if (getEcgWaveForm instanceof CancelablePromise) {
        getEcgWaveForm.cancel();
      }
      if (getAccountData instanceof CancelablePromise) {
        getAccountData.cancel();
      }
    };
  }, [waveformData]);

  return { ecgWaveForm, isLoading, error, accountData };
};

export { useSharedWaveform };
